import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, XCircle, Search, History, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BankValidation() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/reports/bank-validations`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const loadValidation = async (validationId) => {
    try {
      const response = await axios.get(`${API}/reports/bank-validations/${validationId}`);
      setResults(response.data.resultados);
      setShowHistory(false);
      toast.success('Validación cargada');
    } catch (error) {
      toast.error('Error al cargar validación');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast.error('Por favor selecciona un archivo CSV');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/reports/validate-bank-statement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response.data);
      fetchHistory(); // Refresh history
      toast.success('Archivo procesado y guardado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Validación de Estados de Cuenta</h1>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowHistory(!showHistory)}
            className="border-blue-500 text-blue-600"
          >
            <History className="h-4 w-4 mr-2" />
            Historial ({history.length})
          </Button>
        </div>

        {/* History Panel */}
        {showHistory && history.length > 0 && (
          <Card className="p-4 mb-6 bg-blue-50">
            <h3 className="font-semibold mb-3">Validaciones Anteriores</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((val) => (
                <div 
                  key={val.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => loadValidation(val.id)}
                >
                  <div>
                    <p className="font-medium">{val.archivo}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(val.fecha_validacion).toLocaleString()} - {val.validado_por_nombre}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {val.resumen?.posibles_duplicados_excel > 0 && (
                      <Badge className="bg-red-100 text-red-700">{val.resumen.posibles_duplicados_excel} dup. Excel</Badge>
                    )}
                    {val.resumen?.posibles_duplicados_sistema > 0 && (
                      <Badge className="bg-orange-100 text-orange-700">{val.resumen.posibles_duplicados_sistema} dup. Sistema</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Upload Section */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Subir Estado de Cuenta (Banco Industrial)</h2>
          </div>
          
          <p className="text-sm text-slate-600 mb-4">
            Sube el archivo CSV descargado del Banco Industrial para comparar con los pagos del sistema 
            y detectar coincidencias, posibles duplicados y transacciones sin match.
          </p>

          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="max-w-md"
            />
            <Button 
              onClick={handleUpload} 
              disabled={!file || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              {loading ? 'Procesando...' : 'Validar'}
            </Button>
          </div>
          
          {file && (
            <p className="text-sm text-slate-500 mt-2">
              Archivo seleccionado: {file.name}
            </p>
          )}
        </Card>

        {/* Results */}
        {results && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">Coincidencias</span>
                </div>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {results.resumen.coincidencias_exactas}
                </p>
              </Card>
              
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-center gap-2">
                  <Copy className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">Duplicados Excel</span>
                </div>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {results.resumen.posibles_duplicados_excel || 0}
                </p>
                <p className="text-xs text-purple-600">Mismo monto en banco</p>
              </Card>
              
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-800">Duplicados Sistema</span>
                </div>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {results.resumen.posibles_duplicados_sistema || 0}
                </p>
                <p className="text-xs text-red-600">Mismo monto</p>
              </Card>
              
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Banco sin Match</span>
                </div>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  {results.resumen.transacciones_banco_sin_match}
                </p>
              </Card>
              
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-800">Sistema sin Match</span>
                </div>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {results.resumen.pagos_sistema_sin_match}
                </p>
              </Card>
            </div>

            {/* Matches */}
            {results.coincidencias.length > 0 && (
              <Card className="p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-700 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Coincidencias Exactas ({results.coincidencias.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="p-2 text-left">Fecha Banco</th>
                        <th className="p-2 text-left">Monto</th>
                        <th className="p-2 text-left">Descripción Banco</th>
                        <th className="p-2 text-left">Proveedor Sistema</th>
                        <th className="p-2 text-left">Tienda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.coincidencias.slice(0, 20).map((match, idx) => (
                        <tr key={idx} className="border-b hover:bg-green-50/50">
                          <td className="p-2">{match.banco.fecha}</td>
                          <td className="p-2 font-medium">Q{match.banco.monto.toLocaleString()}</td>
                          <td className="p-2 text-xs">{match.banco.descripcion}</td>
                          <td className="p-2">{match.sistema.proveedor}</td>
                          <td className="p-2 text-xs">{match.sistema.tienda}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* DUPLICADOS EN EXCEL - Depósitos con mismo monto */}
            {results.posibles_duplicados_excel && results.posibles_duplicados_excel.length > 0 && (
              <Card className="p-6 mb-6 border-purple-300 bg-purple-50/30">
                <h3 className="text-lg font-semibold text-purple-700 mb-4 flex items-center gap-2">
                  <Copy className="h-5 w-5" />
                  ⚠️ Depósitos con Mismo Monto en Excel ({results.posibles_duplicados_excel.length} grupos)
                </h3>
                <p className="text-sm text-purple-600 mb-4">
                  Estos depósitos del banco tienen el mismo monto. Podrían ser pagos duplicados.
                </p>
                <div className="space-y-4">
                  {results.posibles_duplicados_excel.map((grupo, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-purple-200">
                      <div className="font-bold text-purple-700 mb-3 text-lg">
                        Monto: Q{grupo.monto.toLocaleString()} ({grupo.cantidad} depósitos)
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-purple-100">
                          <tr>
                            <th className="p-2 text-left">Fecha</th>
                            <th className="p-2 text-left">Tipo</th>
                            <th className="p-2 text-left">Descripción</th>
                            <th className="p-2 text-left">No. Doc</th>
                            <th className="p-2 text-left">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grupo.transacciones.map((tx, tIdx) => (
                            <tr key={tIdx} className="border-b hover:bg-purple-50">
                              <td className="p-2">{tx.fecha}</td>
                              <td className="p-2"><Badge>{tx.tipo_transaccion}</Badge></td>
                              <td className="p-2 text-xs">{tx.descripcion}</td>
                              <td className="p-2">{tx.no_doc}</td>
                              <td className="p-2 font-medium">Q{tx.monto.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* DUPLICADOS EN SISTEMA - Pagos con mismo monto */}
            {results.posibles_duplicados_sistema && results.posibles_duplicados_sistema.length > 0 && (
              <Card className="p-6 mb-6 border-red-300 bg-red-50/30">
                <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  ⚠️ Pagos con Mismo Monto en Sistema ({results.posibles_duplicados_sistema.length} grupos)
                </h3>
                <p className="text-sm text-red-600 mb-4">
                  Estos pagos tienen el mismo monto. Verificar si son duplicados.
                </p>
                <div className="space-y-4">
                  {results.posibles_duplicados_sistema.map((grupo, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-red-200">
                      <div className="font-bold text-red-700 mb-3 text-lg">
                        Monto: Q{grupo.monto.toLocaleString()} ({grupo.cantidad} pagos)
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="p-2 text-left">Proveedor</th>
                            <th className="p-2 text-left">Tienda</th>
                            <th className="p-2 text-left">Usuario</th>
                            <th className="p-2 text-left">Fecha</th>
                            <th className="p-2 text-left">Serie/DT</th>
                            <th className="p-2 text-left">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grupo.pagos.map((pago, pIdx) => (
                            <tr key={pIdx} className="border-b hover:bg-red-50">
                              <td className="p-2">{pago.proveedor}</td>
                              <td className="p-2 text-xs">{pago.tienda}</td>
                              <td className="p-2 text-xs">{pago.usuario}</td>
                              <td className="p-2 text-xs">{pago.fecha?.split('T')[0]}</td>
                              <td className="p-2 text-xs">{pago.serie}-{pago.numero_dt}</td>
                              <td className="p-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => navigate(`/payments/${pago.payment_id}`)}
                                >
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Possible Duplicates from Bank */}
            {results.posibles_duplicados_banco && results.posibles_duplicados_banco.length > 0 && (
              <Card className="p-6 mb-6 border-orange-200">
                <h3 className="text-lg font-semibold text-orange-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Depósitos Banco con Múltiples Coincidencias ({results.posibles_duplicados_banco.length})
                </h3>
                <p className="text-sm text-orange-600 mb-4">
                  Estas transacciones del banco coinciden con múltiples pagos en el sistema.
                </p>
                <div className="space-y-4">
                  {results.posibles_duplicados_banco.map((dup, idx) => (
                    <div key={idx} className="bg-orange-50 p-4 rounded-lg">
                      <div className="font-medium mb-2">
                        Banco: Q{dup.banco.monto.toLocaleString()} - {dup.banco.fecha} - {dup.banco.descripcion}
                      </div>
                      <div className="text-sm text-slate-600">
                        <span className="font-medium">Posibles pagos:</span>
                        <ul className="mt-1 space-y-1">
                          {dup.posibles_pagos.map((pago, pIdx) => (
                            <li key={pIdx} className="flex items-center gap-2">
                              <Badge variant="outline">{pago.proveedor}</Badge>
                              <span>Q{pago.monto_sistema.toLocaleString()}</span>
                              <span className="text-xs text-slate-500">{pago.tienda}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Unmatched Bank Transactions */}
            {results.sin_coincidencia_banco.length > 0 && (
              <Card className="p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Transacciones Banco sin Match ({results.sin_coincidencia_banco.length})
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Depósitos en el banco que no coinciden con ningún pago registrado en el sistema.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left">Monto</th>
                        <th className="p-2 text-left">Tipo</th>
                        <th className="p-2 text-left">Descripción</th>
                        <th className="p-2 text-left">No. Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.sin_coincidencia_banco.slice(0, 30).map((tx, idx) => (
                        <tr key={idx} className="border-b hover:bg-blue-50/50">
                          <td className="p-2">{tx.fecha}</td>
                          <td className="p-2 font-medium">Q{tx.monto.toLocaleString()}</td>
                          <td className="p-2"><Badge>{tx.tipo_transaccion}</Badge></td>
                          <td className="p-2 text-xs">{tx.descripcion}</td>
                          <td className="p-2">{tx.no_doc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Unmatched System Payments */}
            {results.sin_coincidencia_sistema.length > 0 && (
              <Card className="p-6 mb-6 border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  Pagos Sistema sin Match ({results.sin_coincidencia_sistema.length})
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Pagos aprobados en el sistema que no tienen transacción correspondiente en el banco.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="p-2 text-left">Proveedor</th>
                        <th className="p-2 text-left">Monto</th>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left">Tienda</th>
                        <th className="p-2 text-left">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.sin_coincidencia_sistema.slice(0, 30).map((pago, idx) => (
                        <tr key={idx} className="border-b hover:bg-red-50/50">
                          <td className="p-2">{pago.proveedor}</td>
                          <td className="p-2 font-medium">Q{pago.monto.toLocaleString()}</td>
                          <td className="p-2 text-xs">{pago.fecha?.split('T')[0]}</td>
                          <td className="p-2 text-xs">{pago.tienda}</td>
                          <td className="p-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(`/payments/${pago.payment_id}`)}
                            >
                              Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
